import { useState, useEffect } from "react";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PortalLayout from "../components/PortalLayout";
import DocumentPreview from "../components/DocumentPreview";
import {
  Users,
  Plus,
  Trash2,
  Upload,
  Eye,
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileText,
  Loader2,
  ShieldCheck,
  Bell,
  Mail,
  MessageSquare,
  AlertCircle,
} from "lucide-react";

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const AADHAAR_REGEX = /^\d{12}$/;

const ProfileManager = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({
    full_name: "",
    date_of_birth: "",
    pan_number: "",
    aadhar_number: "",
    phone: "",
    opt_in_email: true,
    opt_in_whatsapp: false,
  });

  useEffect(() => { fetchProfiles(); }, []);

  const fetchProfiles = async () => {
    try {
      const res = await authFetch(`${API}/profiles`);
      if (res.ok) setProfiles(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required";
    if (!form.date_of_birth) e.date_of_birth = "Date of birth is required";
    if (!form.pan_number) e.pan_number = "PAN number is required";
    else if (!PAN_REGEX.test(form.pan_number)) e.pan_number = "PAN must match format ABCDE1234F";
    if (!form.aadhar_number) e.aadhar_number = "Aadhaar number is required";
    else if (!AADHAAR_REGEX.test(form.aadhar_number)) e.aadhar_number = "Aadhaar must be exactly 12 digits";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/profiles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success("Profile created");
        setForm({ full_name: "", date_of_birth: "", pan_number: "", aadhar_number: "", phone: "", opt_in_email: true, opt_in_whatsapp: false });
        setErrors({});
        setShowForm(false);
        fetchProfiles();
      }
    } catch { toast.error("Failed to create profile"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    try {
      const res = await authFetch(`${API}/profiles/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Deleted"); fetchProfiles(); }
    } catch { toast.error("Failed to delete"); }
  };

  const handleKycUpload = async (profileId, docType, file) => {
    const key = `${profileId}_${docType}`;
    setUploading((prev) => ({ ...prev, [key]: true }));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    try {
      const res = await authFetch(`${API}/profiles/${profileId}/upload-kyc`, {
        method: "POST", body: formData,
      });
      if (res.ok) { toast.success(`${docType} uploaded`); fetchProfiles(); }
      else toast.error("Upload failed");
    } catch { toast.error("Upload error"); }
    finally { setUploading((prev) => ({ ...prev, [key]: false })); }
  };

  const updateNotifPref = async (profileId, prefs) => {
    try {
      const res = await authFetch(`${API}/profiles/${profileId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(prefs),
      });
      if (res.ok) { toast.success("Preferences updated"); fetchProfiles(); }
    } catch { toast.error("Failed to update preferences"); }
  };

  const startEditing = (p) => {
    setEditingId(p.profile_id);
    setEditForm({
      full_name: p.full_name || "",
      date_of_birth: p.date_of_birth || "",
      pan_number: p.pan_number || "",
      aadhar_number: p.aadhar_number || "",
      phone: p.phone || "",
    });
    setErrors({});
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.full_name.trim()) e.full_name = "Full name is required";
    if (editForm.pan_number && !PAN_REGEX.test(editForm.pan_number)) e.pan_number = "PAN must match format ABCDE1234F";
    if (editForm.aadhar_number && !AADHAAR_REGEX.test(editForm.aadhar_number)) e.aadhar_number = "Aadhaar must be exactly 12 digits";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateEdit()) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/profiles/${editingId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (res.ok) { toast.success("Profile updated"); setEditingId(null); fetchProfiles(); }
      else toast.error("Failed to update profile");
    } catch { toast.error("Error updating profile"); }
    finally { setSubmitting(false); }
  };

  const FieldError = ({ msg }) => msg ? (
    <p className="text-[11px] text-[#EF4444] mt-1 flex items-center gap-1" data-testid="field-error">
      <AlertCircle className="w-3 h-3" /> {msg}
    </p>
  ) : null;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
      <div className="spinner"></div>
    </div>
  );

  return (
    <PortalLayout title="Client Profiles">

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
              Manage Profiles
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              Add family members or sister businesses for easy filing
            </p>
          </div>
          <button onClick={() => setShowForm(!showForm)}
            className="soft-btn !px-4 !py-2 whitespace-nowrap shrink-0"
            data-testid="add-profile-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Profile
          </button>
        </div>

        {/* Create Form */}
        {showForm && (
          <div className="soft-card mb-3" data-testid="profile-form">
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Full Name *</label>
                  <Input value={form.full_name} onChange={(e) => { setForm({ ...form, full_name: e.target.value }); setErrors((p) => ({ ...p, full_name: undefined })); }}
                    placeholder="Full legal name" className={`rounded-xl border-[var(--border-light)] ${errors.full_name ? "border-[#EF4444]" : ""}`} data-testid="profile-name" />
                  <FieldError msg={errors.full_name} />
                </div>
                <div>
                  <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Date of Birth *</label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => { setForm({ ...form, date_of_birth: e.target.value }); setErrors((p) => ({ ...p, date_of_birth: undefined })); }}
                    className={`rounded-xl border-[var(--border-light)] ${errors.date_of_birth ? "border-[#EF4444]" : ""}`} data-testid="profile-dob" />
                  <FieldError msg={errors.date_of_birth} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">PAN Card Number *</label>
                  <Input value={form.pan_number} onChange={(e) => { setForm({ ...form, pan_number: e.target.value.toUpperCase() }); setErrors((p) => ({ ...p, pan_number: undefined })); }}
                    placeholder="ABCDE1234F" maxLength={10} className={`rounded-xl border-[var(--border-light)] ${errors.pan_number ? "border-[#EF4444]" : ""}`} data-testid="profile-pan" />
                  <FieldError msg={errors.pan_number} />
                </div>
                <div>
                  <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Aadhaar Number *</label>
                  <Input value={form.aadhar_number} onChange={(e) => { setForm({ ...form, aadhar_number: e.target.value.replace(/\D/g, "") }); setErrors((p) => ({ ...p, aadhar_number: undefined })); }}
                    placeholder="123456789012" maxLength={12} className={`rounded-xl border-[var(--border-light)] ${errors.aadhar_number ? "border-[#EF4444]" : ""}`} data-testid="profile-aadhar" />
                  <FieldError msg={errors.aadhar_number} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Phone Number *</label>
                <Input value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); setErrors((p) => ({ ...p, phone: undefined })); }}
                  placeholder="+91 98765 43210" className={`rounded-xl border-[var(--border-light)] ${errors.phone ? "border-[#EF4444]" : ""}`} data-testid="profile-phone" />
                <FieldError msg={errors.phone} />
              </div>

              {/* Notification Preferences */}
              <div className="p-4 rounded-lg border flex items-center gap-3" style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}>
                <input
                  type="checkbox"
                  id="opt_in_email"
                  checked={form.opt_in_email}
                  onChange={(e) => setForm({ ...form, opt_in_email: e.target.checked })}
                  className="w-4 h-4 text-[#145886] border-[var(--border-light)] rounded focus:ring-[#145886] cursor-pointer"
                  data-testid="notif-email-checkbox"
                />
                <label htmlFor="opt_in_email" className="text-xs font-semibold cursor-pointer select-none" style={{ color: "var(--text-primary)" }}>
                  Receive email updates for filing status and chat messages
                </label>
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={submitting} className="soft-btn disabled:opacity-50" data-testid="save-profile-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  {submitting ? "Saving..." : "Save Profile"}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setErrors({}); }} className="px-5 py-2 text-sm font-bold text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Profiles List */}
        <div className="space-y-4">
          {profiles.length === 0 && !showForm && (
            <div className="text-center py-16 px-4 bg-[var(--background-primary)] border border-dashed border-[var(--border-light)] rounded-[20px]">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-sm border border-[var(--border-light)] flex items-center justify-center">
                <Users className="w-10 h-10 text-[var(--text-muted)]/50" />
              </div>
              <p className="font-bold text-lg mb-2 text-[var(--text-main)]">No profiles yet</p>
              <p className="text-sm mb-3 text-[var(--text-muted)]">Add a profile for yourself or your family members to get started with filing</p>
              <button onClick={() => setShowForm(true)} className="soft-btn mx-auto inline-flex">
                <Plus className="w-4 h-4 mr-2" /> Create First Profile
              </button>
            </div>
          )}

          {profiles.map((p) => {
            const isExpanded = expandedId === p.profile_id;
            const currentPref = p.opt_in_email && p.opt_in_whatsapp ? "both" : p.opt_in_whatsapp ? "whatsapp" : "email";
            return (
              <div key={p.profile_id} className="soft-card !p-0 overflow-hidden" data-testid={`profile-${p.profile_id}`}>
                <button onClick={() => setExpandedId(isExpanded ? null : p.profile_id)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[var(--primary)]/5 transition-all cursor-pointer" data-testid={`profile-toggle-${p.profile_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white shadow-sm border border-[var(--border-light)] flex items-center justify-center text-[var(--primary)] font-bold text-lg">
                      {p.full_name?.[0] || "?"}
                    </div>
                    <div>
                      <span className="font-semibold text-sm block" style={{ color: "var(--text-primary)" }}>{p.full_name || "Unnamed"}</span>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {p.pan_number ? `PAN: ${p.pan_number}` : "PAN not set"} &middot;
                        {p.opt_in_email ? " Email" : ""}{p.opt_in_whatsapp ? " WhatsApp" : ""}{!p.opt_in_email && !p.opt_in_whatsapp ? " No notifications" : ""}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(p.profile_id); }}
                      className="text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`delete-profile-${p.profile_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t px-4 pb-4 pt-3 space-y-4" style={{ borderColor: "var(--border-color)" }}>
                    {editingId === p.profile_id ? (
                      /* EDIT MODE */
                      <div className="space-y-3">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-semibold mb-1 block" style={{ color: "var(--text-primary)" }}>Full Name *</label>
                            <Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                              className={errors.full_name ? "border-[#EF4444]" : ""} data-testid="edit-name" />
                            <FieldError msg={errors.full_name} />
                          </div>
                          <div>
                            <label className="text-xs font-semibold mb-1 block" style={{ color: "var(--text-primary)" }}>Date of Birth</label>
                            <Input type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} data-testid="edit-dob" />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-semibold mb-1 block" style={{ color: "var(--text-primary)" }}>PAN Number</label>
                            <Input value={editForm.pan_number} onChange={(e) => setEditForm({ ...editForm, pan_number: e.target.value.toUpperCase() })}
                              placeholder="ABCDE1234F" maxLength={10} className={errors.pan_number ? "border-[#EF4444]" : ""} data-testid="edit-pan" />
                            <FieldError msg={errors.pan_number} />
                          </div>
                          <div>
                            <label className="text-xs font-semibold mb-1 block" style={{ color: "var(--text-primary)" }}>Aadhaar Number</label>
                            <Input value={editForm.aadhar_number} onChange={(e) => setEditForm({ ...editForm, aadhar_number: e.target.value.replace(/\D/g, "") })}
                              placeholder="123456789012" maxLength={12} className={errors.aadhar_number ? "border-[#EF4444]" : ""} data-testid="edit-aadhar" />
                            <FieldError msg={errors.aadhar_number} />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold mb-1 block" style={{ color: "var(--text-primary)" }}>Phone</label>
                          <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            placeholder="+91 98765 43210" data-testid="edit-phone" />
                        </div>
                        <div className="flex gap-3">
                          <button type="button" onClick={handleUpdate} disabled={submitting}
                            className="soft-btn !px-4 !py-2 text-sm disabled:opacity-50" data-testid="save-edit-btn">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Changes
                          </button>
                          <button type="button" onClick={() => { setEditingId(null); setErrors({}); }} className="px-4 py-2 text-sm font-bold text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      /* VIEW MODE */
                      <>
                    <div className="flex items-center justify-between mb-1">
                      <span></span>
                      <button onClick={() => startEditing(p)}
                        className="px-4 py-1.5 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs font-bold" data-testid={`edit-profile-${p.profile_id}`}>
                        Edit Profile
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-xs font-semibold block mb-0.5" style={{ color: "var(--text-muted)" }}>Date of Birth</span>
                        <span style={{ color: "var(--text-primary)" }}>{p.date_of_birth || "Not set"}</span>
                      </div>
                      <div>
                        <span className="text-xs font-semibold block mb-0.5" style={{ color: "var(--text-muted)" }}>Aadhaar</span>
                        <span style={{ color: "var(--text-primary)" }}>{p.aadhar_number || "Not set"}</span>
                      </div>
                      <div>
                        <span className="text-xs font-semibold block mb-0.5" style={{ color: "var(--text-muted)" }}>Phone</span>
                        <span style={{ color: "var(--text-primary)" }}>{p.phone || "Not set"}</span>
                      </div>
                    </div>

                    {/* KYC Document Uploads */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
                        <ShieldCheck className="w-3.5 h-3.5 text-[#55B848]" /> KYC Documents
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {[
                          { type: "pan_doc", label: "PAN Card", url: p.pan_doc_url },
                          { type: "aadhar_doc", label: "Aadhaar Card", url: p.aadhar_doc_url },
                        ].map((doc) => (
                          <div key={doc.type} className="flex items-center justify-between p-2.5 rounded-lg border hover:shadow-sm transition-all"
                            style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}>
                            <div className="flex items-center gap-2">
                              {doc.url ? <FileText className="w-4 h-4 text-[#55B848]" /> : <CreditCard className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
                              <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>{doc.label}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {doc.url && (
                                <button onClick={() => setPreview({ fileUrl: doc.url, filename: doc.label, contentType: "image" })}
                                  className="p-1 rounded hover:bg-[#145886]/10 transition-colors cursor-pointer" data-testid={`preview-${doc.type}-${p.profile_id}`}>
                                  <Eye className="w-3.5 h-3.5 text-[#145886]" />
                                </button>
                              )}
                              <label className="p-1 rounded hover:bg-[#145886]/10 transition-colors cursor-pointer">
                                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => e.target.files?.[0] && handleKycUpload(p.profile_id, doc.type, e.target.files[0])} />
                                {uploading[`${p.profile_id}_${doc.type}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#145886]" /> : <Upload className="w-3.5 h-3.5 text-[#145886]" />}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notification Preferences */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
                        <Bell className="w-3.5 h-3.5 text-[#145886]" /> Notification Preferences
                      </h4>
                      <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#145886]" />
                          <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>Email Notifications</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateNotifPref(p.profile_id, { opt_in_email: !p.opt_in_email, opt_in_whatsapp: false })}
                          className={`px-4 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer hover:shadow-sm
                            ${p.opt_in_email ? "bg-[#55B848] text-white border-transparent" : "bg-transparent text-[var(--text-muted)] border-[var(--border-color)]"}`}
                          data-testid={`pref-email-toggle-${p.profile_id}`}
                        >
                          {p.opt_in_email ? "Enabled" : "Disabled"}
                        </button>
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {preview && <DocumentPreview fileUrl={preview.fileUrl} filename={preview.filename} contentType={preview.contentType} onClose={() => setPreview(null)} />}
    
    </PortalLayout>
  );
};

export default ProfileManager;
